import React, { useCallback, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import AddModal from './components/common/AddModal';
import Sidebar from './components/common/Sidebar';
import MainIndex from './screens/MainIndex';
import { onMessage } from 'firebase/messaging';
import { messaging } from './utils/firebase';
import { fetchUserNotifications } from './Redux/Reducers/UserNotificationsReducer';
import { isNotificationRead, getNotificationId, getNotificationRedirectUrl } from './utils/notificationUtils';

import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// import socket from './socket/socket';

const theme = createTheme({
    palette: {
        primary: {
            main: '#1f62c4',
        },
        secondary: {
            main: '#282c34',
        },
    },
    typography: {
        fontFamily: '"Inter", sans-serif',

        h1: { fontFamily: '"Manrope", sans-serif' },
        h2: { fontFamily: '"Manrope", sans-serif' },
        h3: { fontFamily: '"Manrope", sans-serif' },
        h4: { fontFamily: '"Manrope", sans-serif' },
        h5: { fontFamily: '"Manrope", sans-serif' },
        h6: { fontFamily: '"Manrope", sans-serif' },
    },
});

function App(props) {
    const location = useLocation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { token: reduxToken } = useSelector((state) => state.AuthReducer);
    const user = useSelector((state) => state.AuthReducer?.user);
    const token = reduxToken || (typeof window !== 'undefined' ? sessionStorage.getItem('admin-token') : null);
    const notifications = useSelector((state) => state.userNotifications?.notifications || []);
    const { theme: currentTheme } = useSelector((state) => state.Mainreducer);
    const activekey = () => location.pathname;
    const recentRealtimeToastKeys = useRef(new Map());

    const showRealtimeNotificationToast = useCallback((title, body, redirectUrl) => {
        const normalizeToastText = (value) => String(value || '').replace(/\s+/g, ' ').trim().toLowerCase();
        const toastKey = `${normalizeToastText(title)}|${normalizeToastText(body)}`;
        const now = Date.now();
        const lastShownAt = recentRealtimeToastKeys.current.get(toastKey);

        // FCM and the notification API polling can report the same event independently.
        if (lastShownAt && now - lastShownAt < 20000) return;

        recentRealtimeToastKeys.current.set(toastKey, now);
        recentRealtimeToastKeys.current.forEach((shownAt, key) => {
            if (now - shownAt >= 20000) recentRealtimeToastKeys.current.delete(key);
        });

        toast.info(
            <div style={{ cursor: redirectUrl ? 'pointer' : 'default' }}>
                <div style={{ fontWeight: 600 }}>{title}</div>
                {body && <div style={{ fontSize: '13px', marginTop: '2px', opacity: 0.9 }}>{body}</div>}
            </div>,
            {
                toastId: toastKey,
                position: 'top-right',
                autoClose: 6000,
                onClick: () => {
                    if (redirectUrl) navigate(redirectUrl);
                },
            }
        );
    }, [navigate]);


    useEffect(() => {
        const handleClickOutside = (event) => {
            const sidebar = document.getElementById('mainsidemenu');
            const toggleButton = document.querySelector('.menu-toggle');
            const overlay = document.getElementById('sidebar-overlay');

            if (sidebar && sidebar.classList.contains('open')) {
                if (
                    !sidebar.contains(event.target) &&
                    !toggleButton?.contains(event.target) &&
                    !event.target.closest('.menu-toggle')
                ) {
                    sidebar.classList.remove('open');
                    if (overlay) {
                        overlay.classList.remove('active');
                    }
                }
            }
        };

        const toggleOverlay = () => {
            const sidebar = document.getElementById('mainsidemenu');
            const overlay = document.getElementById('sidebar-overlay');

            if (sidebar && overlay) {
                if (sidebar.classList.contains('open')) {
                    overlay.classList.add('active');
                } else {
                    overlay.classList.remove('active');
                }
            }
        };

        const observer = new MutationObserver(toggleOverlay);
        const sidebar = document.getElementById('mainsidemenu');

        if (sidebar) {
            observer.observe(sidebar, {
                attributes: true,
                attributeFilter: ['class']
            });
            toggleOverlay();
        }

        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('touchstart', handleClickOutside);

        // Prevent MUI Dialogs from closing when clicking the backdrop container
        const preventDialogClose = (e) => {
            if (e.target && e.target.classList && e.target.classList.contains('MuiDialog-container')) {
                e.stopPropagation();
            }
        };
        document.addEventListener('mousedown', preventDialogClose, true);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('touchstart', handleClickOutside);
            document.removeEventListener('mousedown', preventDialogClose, true);
            observer.disconnect();
        };
    }, []);

    useEffect(() => {
        if (!token) {
            return undefined;
        }

        // Fetch immediately
        dispatch(fetchUserNotifications({ type: 'all', page: 1, limit: 10 }));

        // Realtime background polling every 12 seconds for live notifications
        const intervalId = setInterval(() => {
            dispatch(fetchUserNotifications({ type: 'all', page: 1, limit: 10 }));
        }, 12000);

        let unsubscribe = null;

        try {
            unsubscribe = onMessage(messaging, (payload) => {
                dispatch(fetchUserNotifications({ type: 'all', page: 1, limit: 10 }));

                const title = payload?.notification?.title || payload?.data?.title || 'New notification';
                const body = payload?.notification?.body || payload?.data?.body || payload?.data?.description || '';
                const redirectUrl = getNotificationRedirectUrl(payload?.data || payload, user);

                showRealtimeNotificationToast(title, body, redirectUrl);
            });
        } catch (error) {
            console.log('FCM foreground listener error:', error);
        }

        return () => {
            clearInterval(intervalId);
            if (typeof unsubscribe === 'function') {
                unsubscribe();
            }
        };
    }, [dispatch, token, navigate, user, showRealtimeNotificationToast]);

    // Track new unread notifications received in realtime and show toast
    const isFirstNotificationRun = useRef(true);
    const seenNotificationIds = useRef(new Set());

    useEffect(() => {
        if (!Array.isArray(notifications) || notifications.length === 0) {
            return;
        }

        if (isFirstNotificationRun.current) {
            notifications.forEach((item) => {
                const id = getNotificationId(item);
                if (id) seenNotificationIds.current.add(String(id));
            });
            isFirstNotificationRun.current = false;
            return;
        }

        const newUnread = notifications.find((item) => {
            const id = getNotificationId(item);
            return id && !seenNotificationIds.current.has(String(id)) && !isNotificationRead(item);
        });

        // Add all current IDs to seen set
        notifications.forEach((item) => {
            const id = getNotificationId(item);
            if (id) seenNotificationIds.current.add(String(id));
        });

        if (newUnread) {
            const title = newUnread.title || 'New Content Pending Approval';
            const desc = newUnread.description || newUnread.message || newUnread.body || '';
            const redirectUrl = getNotificationRedirectUrl(newUnread, user);

            showRealtimeNotificationToast(title, desc, redirectUrl);
        }
    }, [notifications, navigate, user, showRealtimeNotificationToast]);

    return (
        <ThemeProvider theme={theme}>
            <div id="ebazar-layout" className={currentTheme}>
                <ToastContainer />
                <div
                    id="sidebar-overlay"
                    className="sidebar-overlay"
                    onClick={() => {
                        const sidebar = document.getElementById('mainsidemenu');
                        const overlay = document.getElementById('sidebar-overlay');
                        if (sidebar) sidebar.classList.remove('open');
                        if (overlay) overlay.classList.remove('active');
                    }}
                >
                </div>

                {token && <Sidebar activekey={activekey()} history={props.history} />}
                {token && <AddModal />}

                <MainIndex activekey={activekey()} />
            </div>
        </ThemeProvider>
    );
}

export default App;     
